import { createTheme } from '@mui/material/styles';

// Beginner-friendly theme inspired by Taxfix with your original brand colors
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#ff4900', // Your original orange brand color
            light: '#ff6933',
            dark: '#cc3a00',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#e0e0e0', // Back to your original secondary
            light: '#ffd55f',
            dark: '#d6a800',
            contrastText: '#212529',
        },
        background: {
            default: '#0D0D0D', // Darker for content area
            paper: '#0D0D0D', // Darker for content cards
        },
        text: {
            primary: '#FFFFFF', // High contrast white for readability
            secondary: '#FFFFFF', // Your original - both white for maximum contrast
        },
        error: {
            main: '#ef2323', // Your original red
            light: '#f15858',
            dark: '#c51d1d',
            contrastText: '#e0e0e0',
        },
        warning: {
            main: '#ffca27', // Your original sunny yellow
            contrastText: '#212529',
        },
        info: {
            main: '#9ca3af', // Your original light grey
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#28A745', // Your original profit green
            light: '#23ef37',
            dark: '#1E7E34',
            contrastText: '#FFFFFF',
        },
        divider: '#333333', // Your original divider
        grey: {
            // Your original grey palette - warmer than shadcn
            50: '#F8F9FA',
            100: '#E9ECEF',
            200: '#d1d5db',
            300: '#A0A0A0', // Your alternate text color
            400: '#6c757d', // Your gunmetal grey
            500: '#495057',
            600: '#343A40',
            700: '#212529',
            800: '#151515',
            900: '#0D0D0D',
            A100: '#252525', // Your alternate content background
            A200: '#2a2a2a', // Your secondary divider
            A400: '#333333', // Your content border
            A700: '#111111',
        },
    },
    typography: {
        // Back to your original Montserrat font
        fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeightRegular: 'bold', // Your original - bold by default
        fontWeightMedium: 500,
        fontWeightBold: 600,
        // Larger sizes for better readability (Taxfix-inspired)
        h1: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '2.5rem', // Larger
            fontWeight: 700,
            color: '#FFFFFF',
        },
        h2: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '2rem', // Larger
            fontWeight: 700,
            color: '#FFFFFF',
        },
        h3: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.75rem', // Larger
            fontWeight: 700,
            color: '#FFFFFF',
        },
        h4: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.5rem', // Larger
            fontWeight: 600,
            color: '#FFFFFF',
        },
        h5: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.25rem', // Larger
            fontWeight: 600,
            color: '#FFFFFF',
        },
        h6: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.125rem', // Larger
            fontWeight: 600,
            color: '#FFFFFF',
        },
        subtitle1: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem', // Larger base
            fontWeight: 'bold',
            color: '#FFFFFF',
        },
        subtitle2: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            color: '#FFFFFF',
        },
        body1: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem', // Larger for better readability
            lineHeight: 1.6,
            fontWeight: 'bold',
            color: '#FFFFFF',
        },
        body2: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            fontWeight: 'bold',
            color: '#FFFFFF',
        },
        button: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem', // Larger button text
            fontWeight: 600,
            textTransform: 'none',
        },
    },
    shape: {
        borderRadius: 8, // More subtle, modern corners
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#0D0D0D', // Darker for content cards
                    borderRadius: 8, // More subtle, modern corners
                    boxShadow: 'none', // Completely flat - no shadows
                    border: '1px solid rgba(255, 255, 255, 0.1)', // More subtle border
                    padding: '16px', // Better breathing room
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#0D0D0D', // Darker for content paper elements
                    borderRadius: 8, // More subtle corners
                    boxShadow: 'none', // Flat design
                    '--Paper-overlay': 'none !important',
                    '&.MuiMenu-paper, &.MuiPopover-paper, &.MuiPickersPopper-paper': {
                        backgroundColor: '#0D0D0D', // Keep menus dark
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)', // Only for floating elements like menus
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8, // Consistent with cards
                    padding: '12px 24px', // More generous padding
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    minHeight: '48px', // Larger touch targets
                    boxShadow: 'none', // Flat design
                    '&:hover': {
                        boxShadow: 'none', // Keep it flat
                    },
                },
                contained: {
                    boxShadow: 'none', // Completely flat
                    '&:hover': {
                        boxShadow: 'none', // No elevation on hover
                        transform: 'none', // No movement effects
                        opacity: 0.9, // Simple opacity change instead
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                },
                large: {
                    padding: '16px 32px',
                    fontSize: '1.125rem',
                    minHeight: '56px',
                },
            },
            variants: [
                {
                    props: { variant: 'bulkAction' },
                    style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        color: '#FFFFFF',
                        padding: '12px 24px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        },
                    },
                },
            ],
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                        fontSize: '1rem',
                        '& fieldset': {
                            borderColor: '#333333',
                            borderWidth: '1.5px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#555555',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#ff4900',
                            borderWidth: '2px',
                        },
                        '& input': {
                            padding: '16px 14px', // More generous padding
                        },
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: '1rem',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontSize: '0.875rem',
                    height: 32, // Larger
                    fontWeight: 500,
                    padding: '0 12px',
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                enterTouchDelay: 0,
                leaveTouchDelay: 2500,
                disableTouchListener: false,
                disableHoverListener: false,
                disableFocusListener: false,
                PopperProps: {
                    modifiers: [
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                boundary: 'viewport',
                                padding: 8,
                            },
                        },
                        {
                            name: 'flip',
                            enabled: true,
                        },
                    ],
                },
            },
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#1a1a1a',
                    color: '#FFFFFF',
                    fontSize: '0.875rem',
                    borderRadius: 8,
                    padding: '8px 12px',
                },
                arrow: {
                    color: '#1a1a1a',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#151515', // Lighter for top header
                    backgroundImage: 'none',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    borderBottom: '1px solid #333333',
                    minHeight: '64px',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#151515', // Lighter for sidebar
                    borderRight: '1px solid #333333',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    marginTop: 8,
                    border: '1px solid #333333',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '1rem', // Larger
                    padding: '12px 16px', // More generous
                    minHeight: '48px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 73, 0, 0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 73, 0, 0.15)',
                        },
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#0D0D0D', // Darker for dialogs (content)
                    borderRadius: 16, // Extra rounded
                    padding: '24px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    fontSize: '1rem',
                    padding: '16px 24px',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    borderRadius: 8, // Match our card border radius
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', // Subtle dark theme skeleton
                },
                rectangular: {
                    borderRadius: 8, // Consistent with our flat design
                },
            },
        },
    },
});

const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#ff4900', // Your original orange brand color
            light: '#ff6933',
            dark: '#cc3a00',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#6c757d', // Warmer than shadcn grays
            light: '#a1a1aa',
            dark: '#495057',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#ffffff', // Clean white like Taxfix
            paper: '#ffffff', // Clean white cards
        },
        text: {
            primary: '#212529', // Warmer dark text (your original color)
            secondary: '#6c757d', // Your original secondary
        },
        error: {
            main: '#dc2626',
            light: '#ef4444',
            dark: '#b91c1c',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#f59e0b',
            contrastText: '#FFFFFF',
        },
        info: {
            main: '#3b82f6',
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#28A745', // Your original green
            light: '#23ef37',
            dark: '#1E7E34',
            contrastText: '#FFFFFF',
        },
        divider: '#D1D5DB', // Warmer divider
        grey: {
            // Warmer grays, not shadcn's cold ones
            50: '#F8F9FA',
            100: '#E9ECEF',
            200: '#DEE2E6',
            300: '#CED4DA',
            400: '#ADB5BD',
            500: '#6C757D',
            600: '#495057',
            700: '#343A40',
            800: '#212529',
            900: '#0D0D0D',
            A100: '#f8f9fa',
            A200: '#e9ecef',
            A400: '#dee2e6',
            A700: '#ced4da',
        },
    },
    typography: {
        // Back to your original Montserrat
        fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeightRegular: 'bold', // Your original
        fontWeightMedium: 500,
        fontWeightBold: 600,
        // Larger sizes for better readability
        h1: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '2.5rem',
            fontWeight: 700,
            color: '#212529',
        },
        h2: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '2rem',
            fontWeight: 700,
            color: '#212529',
        },
        h3: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#212529',
        },
        h4: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#212529',
        },
        h5: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#212529',
        },
        h6: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#212529',
        },
        subtitle1: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#212529',
        },
        subtitle2: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            color: '#6c757d',
        },
        body1: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem', // Larger for better readability
            lineHeight: 1.6,
            fontWeight: 'bold',
            color: '#212529',
        },
        body2: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            fontWeight: 'bold',
            color: '#212529',
        },
        button: {
            fontFamily: '"Montserrat Classic", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
        },
    },
    shape: {
        borderRadius: 8, // More subtle, modern corners
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    borderRadius: 8, // More subtle corners
                    boxShadow: 'none', // Completely flat - no shadows
                    border: '1px solid rgba(0, 0, 0, 0.08)', // More subtle border
                    padding: '16px', // Better breathing room
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    borderRadius: 8, // More subtle corners
                    boxShadow: 'none', // Flat design
                    '--Paper-overlay': 'none !important',
                    '&.MuiMenu-paper, &.MuiPopover-paper, &.MuiPickersPopper-paper': {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', // Only for floating elements like menus
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8, // Consistent with cards
                    padding: '12px 24px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    minHeight: '48px',
                    boxShadow: 'none', // Flat design
                    '&:hover': {
                        boxShadow: 'none', // Keep it flat
                    },
                },
                contained: {
                    boxShadow: 'none', // Completely flat
                    '&:hover': {
                        boxShadow: 'none', // No elevation on hover
                        transform: 'none', // No movement effects
                        opacity: 0.9, // Simple opacity change instead
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    borderColor: '#D1D5DB',
                    '&:hover': {
                        borderWidth: '1.5px',
                        borderColor: '#ADB5BD',
                        backgroundColor: '#F8F9FA',
                    },
                },
                large: {
                    padding: '16px 32px',
                    fontSize: '1.125rem',
                    minHeight: '56px',
                },
            },
            variants: [
                {
                    props: { variant: 'bulkAction' },
                    style: {
                        backgroundColor: '#E9ECEF',
                        color: '#212529',
                        padding: '12px 24px',
                        '&:hover': {
                            backgroundColor: '#DEE2E6',
                        },
                    },
                },
            ],
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                        fontSize: '1rem',
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                            borderColor: '#D1D5DB',
                            borderWidth: '1.5px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#ADB5BD',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#ff4900',
                            borderWidth: '2px',
                        },
                        '& input': {
                            padding: '16px 14px',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: '1rem',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontSize: '0.875rem',
                    height: 32,
                    fontWeight: 500,
                    padding: '0 12px',
                },
                outlined: {
                    borderWidth: '1.5px',
                    borderColor: '#D1D5DB',
                    '&:hover': {
                        backgroundColor: '#F8F9FA',
                    },
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                enterTouchDelay: 0,
                leaveTouchDelay: 2500,
                disableTouchListener: false,
                disableHoverListener: false,
                disableFocusListener: false,
                PopperProps: {
                    modifiers: [
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                boundary: 'viewport',
                                padding: 8,
                            },
                        },
                        {
                            name: 'flip',
                            enabled: true,
                        },
                    ],
                },
            },
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#343A40',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    borderRadius: 8,
                    padding: '8px 12px',
                },
                arrow: {
                    color: '#343A40',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    backgroundImage: 'none',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    borderBottom: '1px solid #D1D5DB',
                    color: '#212529',
                    minHeight: '64px',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#ffffff',
                    borderRight: '1px solid #D1D5DB',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    marginTop: 8,
                    border: '1px solid #D1D5DB',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                    padding: '12px 16px',
                    minHeight: '48px',
                    '&:hover': {
                        backgroundColor: '#F8F9FA',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 73, 0, 0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 73, 0, 0.15)',
                        },
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#ffffff',
                    borderRadius: 16,
                    padding: '24px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    fontSize: '1rem',
                    padding: '16px 24px',
                },
                standardError: {
                    backgroundColor: '#F8D7DA',
                    color: '#721C24',
                    border: '1px solid #F1AABA',
                },
                standardWarning: {
                    backgroundColor: '#FFF3CD',
                    color: '#856404',
                    border: '1px solid #FFEAA7',
                },
                standardInfo: {
                    backgroundColor: '#D1ECF1',
                    color: '#0C5460',
                    border: '1px solid #B8DAFF',
                },
                standardSuccess: {
                    backgroundColor: '#D4EDDA',
                    color: '#155724',
                    border: '1px solid #C3E6CB',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    borderRadius: 8, // Match our card border radius  
                    backgroundColor: 'rgba(0, 0, 0, 0.08)', // Subtle light theme skeleton
                },
                rectangular: {
                    borderRadius: 8, // Consistent with our flat design
                },
            },
        },
    },
});

// Export both themes as named exports
export { darkTheme, lightTheme };

// Also export darkTheme as default for backwards compatibility
export default darkTheme;